import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, Pause, Radio, WifiOff } from "lucide-react";
import { transcriptStreamClient } from "../../../../utils/TranscriptStreamClient";
import { LiveTranscriptSegment, TranscriptStreamStatus } from "../../../../types";

/**
 * LiveTranscriptPanel (WS-01)
 *
 * Right-hand pane of the recording view: renders the live transcript
 * store owned by TranscriptStreamClient. Read-only — auto-scroll pinned
 * to the latest line, with a "jump to latest" affordance once the user
 * scrolls up. Finalized lines render normally; the in-progress partial
 * renders as a muted tail that firms up in place.
 */

// Same palette as TranscriptionViewer so live speakers and the
// post-session mapped names read as the same people.
const SPEAKER_COLORS = [
    { bg: 'bg-violet-100', text: 'text-violet-700' },
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-green-100', text: 'text-green-700' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-rose-100', text: 'text-rose-700' },
];

// Keep the DOM bounded on long sessions; the full transcript is
// persisted server-side regardless of what is rendered here.
const MAX_RENDERED_STABLE_SEGMENTS = 500;
const SILENCE_HINT_AFTER_MS = 30_000;
const NEAR_BOTTOM_PX = 40;

const formatTimestamp = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface LiveTranscriptPanelProps {
    isRecording: boolean;
}

const LiveTranscriptPanel: React.FC<LiveTranscriptPanelProps> = ({ isRecording }) => {
    const [, setTick] = useState(0);
    const [pinned, setPinned] = useState(true);
    const [silenceHint, setSilenceHint] = useState(false);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const recordingStartedAt = useRef<number>(Date.now());

    // Re-render on every store change (segments, status, stall flag).
    useEffect(() => {
        const unsubscribe = transcriptStreamClient.subscribe(() =>
            setTick((t) => t + 1)
        );
        return unsubscribe;
    }, []);

    const segments = transcriptStreamClient.getSegments();
    const status: TranscriptStreamStatus = transcriptStreamClient.getStatus();
    const stalled = transcriptStreamClient.isStalled();

    // "Not hearing you" hint: recording, stream live, but no text yet.
    useEffect(() => {
        if (!isRecording) return;
        recordingStartedAt.current = Date.now();
        const timer = setInterval(() => {
            const noText = transcriptStreamClient.getSegments().length === 0;
            const elapsed = Date.now() - recordingStartedAt.current;
            setSilenceHint(noText && elapsed >= SILENCE_HINT_AFTER_MS);
        }, 5_000);
        return () => clearInterval(timer);
    }, [isRecording]);

    // Speaker naming by first appearance, stable within the session.
    const speakerNames = useMemo(() => {
        const names = new Map<string, number>();
        for (const segment of segments) {
            if (segment.speaker && !names.has(segment.speaker)) {
                names.set(segment.speaker, names.size);
            }
        }
        return names;
    }, [segments]);

    const stable = segments.filter((s) => s.isStable);
    const partial = segments.length && !segments[segments.length - 1].isStable
        ? segments[segments.length - 1]
        : null;
    const overflow = Math.max(0, stable.length - MAX_RENDERED_STABLE_SEGMENTS);
    const rendered = overflow > 0 ? stable.slice(overflow) : stable;

    // Auto-scroll while pinned near the bottom; user scroll unpins.
    useEffect(() => {
        const el = scrollRef.current;
        if (el && pinned) {
            el.scrollTop = el.scrollHeight;
        }
    });

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const nearBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
        setPinned(nearBottom);
    };

    const jumpToLatest = () => {
        setPinned(true);
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    };

    const speakerChip = (speaker: string | null) => {
        if (!speaker) return null;
        const index = speakerNames.get(speaker) ?? 0;
        const color = SPEAKER_COLORS[index % SPEAKER_COLORS.length];
        return (
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}>
                Speaker {index + 1}
            </span>
        );
    };

    const statusPill = () => {
        switch (status) {
            case "live":
                return (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Live
                    </span>
                );
            case "paused":
                return (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                        <Pause className="w-3.5 h-3.5" />
                        Paused
                    </span>
                );
            case "connecting":
            case "reconnecting":
                return (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                        <Radio className="w-3.5 h-3.5 animate-pulse" />
                        {status === "connecting" ? "Connecting…" : "Reconnecting…"}
                    </span>
                );
            case "interrupted":
                return (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                        <WifiOff className="w-3.5 h-3.5" />
                        Interrupted
                    </span>
                );
            default:
                return null;
        }
    };

    if (status === "unavailable") {
        return (
            <div className="flex flex-col h-full items-center justify-center text-center px-6">
                <p className="text-sm text-gray-400">
                    Live transcript is not available.
                    <br />
                    Your recording is unaffected.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Live Transcript</h3>
                {statusPill()}
            </div>

            {/* Inline notices — the recording itself is never at risk here. */}
            {status === "interrupted" && (
                <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2" role="status">
                    Live transcript interrupted — your recording continues and the
                    full transcript will be available after the session.
                </div>
            )}
            {stalled && status === "live" && (
                <div className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2" role="status">
                    Connection is slow — the live transcript may fall behind.
                </div>
            )}

            {/* Transcript feed. Deliberately NOT aria-live: announcing every
                partial makes screen readers unusable; the role=status lines
                above carry state changes instead. */}
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto min-h-0 py-3 pr-1"
            >
                <div className="flex flex-col justify-end min-h-full gap-3">
                    {overflow > 0 && (
                        <div className="text-xs text-gray-400 text-center">
                            Showing the most recent transcript ({overflow} earlier lines hidden)
                        </div>
                    )}

                    {rendered.length === 0 && !partial && (
                        <div className="text-sm text-gray-400 text-center">
                            {status === "paused"
                                ? "Paused — the transcript resumes when you do."
                                : silenceHint
                                    ? "Not hearing anything yet — check your microphone."
                                    : "Listening…"}
                        </div>
                    )}

                    {rendered.map((segment, i) => {
                        const previous = i > 0 ? rendered[i - 1] : null;
                        const showSpeaker =
                            segment.speaker !== (previous ? previous.speaker : undefined);
                        return (
                            <div key={segment.id} dir="auto">
                                {showSpeaker && (
                                    <div className="flex items-center gap-2 mb-1">
                                        {speakerChip(segment.speaker)}
                                        <span className="text-xs text-gray-400 font-mono">
                                            {formatTimestamp(segment.absStartMs)}
                                        </span>
                                    </div>
                                )}
                                <p className="text-sm text-gray-800 leading-relaxed">
                                    {segment.text}
                                </p>
                            </div>
                        );
                    })}

                    {partial && (
                        <p dir="auto" className="text-sm text-gray-400 italic leading-relaxed">
                            {partial.text}
                        </p>
                    )}
                </div>
            </div>

            {/* Jump to latest */}
            {!pinned && (
                <button
                    onClick={jumpToLatest}
                    className="absolute bottom-6 right-6 flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-full shadow-lg hover:bg-gray-900 transition-colors"
                >
                    <ArrowDown className="w-3.5 h-3.5" />
                    Jump to latest
                </button>
            )}
        </div>
    );
};

export default LiveTranscriptPanel;
